import {
    isDrawingFile,
    isImageFile,
    isOpenAPIFile,
    isPngOrJpgFile,
    type NativeFile,
} from '@gitbook/app-database';
import type { FilesDropZoneAccept } from '@gitbook/spine-react';
import {
    CSV_FILE_EXTENSIONS,
    CSV_MIME_TYPES,
    DOCX_FILE_EXTENSIONS,
    DOCX_MIME_TYPES,
    HTML_FILE_EXTENSIONS,
    HTML_MIME_TYPES,
    MARKDOWN_FILE_EXTENSIONS,
    MARKDOWN_MIME_TYPES,
    ZIP_FILE_EXTENSIONS,
    ZIP_MIME_TYPES,
} from '@gitbook/util-files';
import { assertNever } from '@gitbook/util-ts';

let inputElement: HTMLInputElement | null = null;
let onChangeEventListener: EventListener | null = null;

/**
 * Prompt the user to select files from its system.
 */
export async function promptFile(
    options: { multiple?: boolean; accept?: FilesDropZoneAccept } = {}
): Promise<File[]> {
    const { multiple = false, accept = 'files' } = options;

    // Otherwise fallback to DOM API
    if (!inputElement) {
        inputElement = document.createElement('input');
        inputElement.type = 'file';
    }

    const input = inputElement;

    input.value = '';
    input.multiple = multiple;

    input.accept = (() => {
        switch (accept) {
            case 'drawings':
                return 'image/svg+xml, .svg';
            case 'openapi':
                return 'application/json, .yml, .yaml';
            case 'images':
                return 'image/*';
            case 'png-jpg-images':
                return 'image/jpeg, image/png';
            case 'markdown|html|docx|zip':
                return [
                    ...MARKDOWN_MIME_TYPES,
                    ...MARKDOWN_FILE_EXTENSIONS,
                    ...HTML_MIME_TYPES,
                    ...HTML_FILE_EXTENSIONS,
                    ...DOCX_MIME_TYPES,
                    ...DOCX_FILE_EXTENSIONS,
                    ...ZIP_MIME_TYPES,
                    ...ZIP_FILE_EXTENSIONS,
                ].join(', ');
            case 'csv':
                return [...CSV_MIME_TYPES, ...CSV_FILE_EXTENSIONS].join(', ');
            case 'fonts':
            case 'files':
            case undefined:
                return '*/*';
        }
    })();

    return new Promise((resolve) => {
        // Ensure that the previously attached change listener is removed before adding a new one.
        // This is needed as we reuse the same input element reference across multiple promptFile calls.
        // Without removing the listener, we risk accumulating onChange event handlers on the input element.
        // For instance, if the user cancels the system file picker dialogue
        // and eventually pick a file later at some point, all the previously added event listeners will be triggered,
        // resulting in duplicate file uploads.
        if (onChangeEventListener) {
            input.removeEventListener('change', onChangeEventListener);
        }

        onChangeEventListener = () => {
            const fileList = input.files;
            resolve(Array.from(fileList ?? []).filter((file) => isFileAccepted(file, accept)));
        };

        input.addEventListener('change', onChangeEventListener, {
            once: true,
        });

        // If the user cancels the file picker, we resolve with an empty array.
        input.addEventListener('cancel', () => {
            resolve([]);
        });

        input.click();
    });
}

/**
 * Test if a file matches the accept option of the files manager.
 */
export function isFileAccepted(file: NativeFile, accept: FilesDropZoneAccept): boolean {
    switch (accept) {
        case 'images':
            return isImageFile(file);
        case 'png-jpg-images':
            return isPngOrJpgFile(file);
        case 'drawings':
            return isDrawingFile(file);
        case 'openapi':
            return isOpenAPIFile(file);
        case 'csv':
        case 'fonts':
        case 'files':
        case 'markdown|html|docx|zip':
            return true;
        default:
            assertNever(accept);
    }
}
